import { Controller } from "@hotwired/stimulus"

// Drives the Instagram creative-viewer modal on the Social Media Insights tab
// (QAN-363 part 2). Clicking a media row hydrates the modal from a pre-rendered
// JSON <script> block keyed by data-media-id, then handles carousel navigation,
// Escape / backdrop close, and a neutral placeholder when a post has no creative
// asset URL (Graph media_url can be blank or expired — never a mock image).
//
// Markup pattern (all inside one data-controller="social-post-modal" scope):
//   <tr data-action="click->social-post-modal#open" data-media-id="17…">…</tr>
//   <div data-social-post-modal-target="scrim" class="sm-scrim">
//     <script class="sm-detail" data-media-id="17…" type="application/json">{…}</script>
//     …modal shell with image/caption/cells/permalink/counter/prev/next targets…
//   </div>
export default class extends Controller {
  static targets = [
    "scrim", "modal", "plate", "image", "placeholder",
    "caption", "cells", "permalink", "counter", "prev", "next"
  ]

  connect() {
    this.slides = []
    this.index = 0
  }

  open(event) {
    const row = event.currentTarget
    const id = row.getAttribute("data-media-id")
    const detail = this._detailFor(id)
    if (!detail) return

    this._hydrate(detail)
    this.scrimTarget.classList.add("open")
  }

  close() {
    this.scrimTarget.classList.remove("open")
  }

  // Close only when the click is on the scrim itself, not the modal body.
  backdrop(event) {
    if (event.target === this.scrimTarget) this.close()
  }

  // Stop clicks inside the modal from bubbling to the scrim's backdrop handler.
  stop(event) {
    event.stopPropagation()
  }

  prev(event) {
    event.stopPropagation()
    if (this.slides.length < 2) return
    this.index = (this.index - 1 + this.slides.length) % this.slides.length
    this._renderSlide()
  }

  next(event) {
    event.stopPropagation()
    if (this.slides.length < 2) return
    this.index = (this.index + 1) % this.slides.length
    this._renderSlide()
  }

  // ---- internals ----------------------------------------------------------

  _detailFor(id) {
    const block = this.scrimTarget.querySelector(`script.sm-detail[data-media-id="${id}"]`)
    if (!block) return null
    try {
      return JSON.parse(block.textContent)
    } catch (_e) {
      return null
    }
  }

  _hydrate(detail) {
    // Build the slide list: carousel children if present, else the single asset.
    const children = Array.isArray(detail.children) ? detail.children.filter(Boolean) : []
    this.slides = children.length ? children : [detail.media_url, detail.thumbnail_url].filter(Boolean)
    this.index = 0
    this._renderSlide()

    this.captionTarget.textContent = detail.caption || ""

    this.cellsTarget.innerHTML = ""
    ;(detail.cells || []).forEach((cell) => {
      const wrap = document.createElement("div")
      wrap.className = "cell"
      const label = document.createElement("div")
      label.className = "label"
      label.textContent = cell.label
      const value = document.createElement("div")
      value.className = "value"
      value.textContent = cell.value
      wrap.appendChild(label)
      wrap.appendChild(value)
      this.cellsTarget.appendChild(wrap)
    })

    if (detail.permalink) {
      this.permalinkTarget.href = detail.permalink
      this.permalinkTarget.style.display = "block"
    } else {
      this.permalinkTarget.style.display = "none"
    }

    const multi = this.slides.length > 1
    this.prevTarget.style.display = multi ? "block" : "none"
    this.nextTarget.style.display = multi ? "block" : "none"
  }

  _renderSlide() {
    const url = this.slides[this.index]
    if (url) {
      this.imageTarget.src = url
      this.imageTarget.style.display = "block"
      this.placeholderTarget.style.display = "none"
    } else {
      this.imageTarget.removeAttribute("src")
      this.imageTarget.style.display = "none"
      this.placeholderTarget.style.display = "block"
    }
    this.counterTarget.textContent = this.slides.length > 1
      ? `${String(this.index + 1).padStart(2, "0")} / ${String(this.slides.length).padStart(2, "0")}`
      : ""
  }
}
